import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useMemo, useState, useEffect } from 'react'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import * as S from './styles'
import { TextPill } from 'components/FbUI/TextPill'
import {
  ActionsDropdown,
  createEditAction,
  createCopyAction,
  createToggleActiveAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import FbButton from 'components/FbUI/FbButton'
import { CreateAutomationModal } from './AutomationModal/CreateAutomationModal'
import { EditAutomationModal } from './AutomationModal/EditAutomationModal'
import { CopyAutomationModal } from './AutomationModal/CopyAutomationModal'
import { Automation } from 'models/automation'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import confirm from 'components/dialogConfirm'

export function AutomationsTable() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAutomationId, setEditingAutomationId] = useState<number | null>(
    null
  )
  const [copyingAutomationId, setCopyingAutomationId] = useState<number | null>(
    null
  )
  const [automations, setAutomations] = useState<Automation[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const api = apiService()

  const fetchAutomations = async () => {
    try {
      setIsLoading(true)
      const response = await api.getAutomations()
      setAutomations(response.results || [])
    } catch (err) {
      console.error('Failed to fetch automations:', err)
      toast.error('Failed to load automations')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAutomations()
  }, [])

  const handleDelete = async (automationId: number) => {
    const confirmed = await confirm(
      'Are you sure you want to delete this automation?',
      'Delete Automation',
      'This action cannot be undone.',
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )

    if (!confirmed) {
      return
    }

    try {
      await api.deleteAutomation(automationId)
      toast.success('Automation deleted successfully')
      fetchAutomations()
    } catch (err) {
      console.error('Failed to delete automation:', err)
      toast.error('Failed to delete automation')
    }
  }

  const handleToggleActive = async (
    automationId: number,
    currentActiveState: boolean
  ) => {
    try {
      await api.partialUpdateAutomation(automationId, {
        is_active: !currentActiveState,
      })
      toast.success(
        `Automation ${!currentActiveState ? 'enabled' : 'disabled'} successfully`
      )
      fetchAutomations()
    } catch (err) {
      console.error('Failed to update automation:', err)
      toast.error('Failed to update automation')
    }
  }
  const columns = useMemo<ColumnDef<Automation, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Automation Name',
        size: 300,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 200,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 150,
        cell: (info) => {
          if (info.getValue()) {
            return (
              <TextPill textColor="#13462C" backgroundColor="#D1E8D5">
                Active
              </TextPill>
            )
          } else {
            return (
              <TextPill textColor="#464646" backgroundColor="#E3E1DA">
                Disabled
              </TextPill>
            )
          }
        },
      },
      {
        accessorFn: (row) =>
          `${row.created_by.first_name} ${row.created_by.last_name}`,
        header: 'Created By',
        size: 150,
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        size: 150,
        cell: (info) => {
          const date = new Date(info.getValue())
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        cell: (info) => (
          <ActionsDropdown
            actions={[
              createEditAction(() => {
                setEditingAutomationId(info.row.original.id)
              }),
              createCopyAction(() => {
                setCopyingAutomationId(info.row.original.id)
              }),
              createToggleActiveAction(
                () =>
                  handleToggleActive(
                    info.row.original.id,
                    info.row.original.is_active
                  ),
                info.row.original.is_active
              ),
              createDeleteAction(() => {
                handleDelete(info.row.original.id)
              }),
            ]}
          />
        ),
      },
    ],
    [handleDelete, handleToggleActive]
  )

  const descriptionText = `
  Manage your business rule automations. Create, edit, and monitor automated actions that trigger based on deal events and conditions.
  `

  return (
    <DataTableContainer>
      <ClientDataTable
        tableHeader={
          <S.SettingsTableHeader>
            <OverflownTextTooltip
              tooltipText={descriptionText}
              maxLines={4}
              placement="bottom"
            >
              <S.TableDescriptionText>{descriptionText}</S.TableDescriptionText>
            </OverflownTextTooltip>
            <FbButton
              variant="primary"
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              Create Automation
            </FbButton>
          </S.SettingsTableHeader>
        }
        height={'100%'}
        data={automations}
        columns={columns}
        loading={isLoading}
      />

      <CreateAutomationModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onAutomationCreated={fetchAutomations}
      />

      {editingAutomationId && (
        <EditAutomationModal
          show={!!editingAutomationId}
          handleClose={() => setEditingAutomationId(null)}
          automationId={editingAutomationId}
          onAutomationUpdated={fetchAutomations}
        />
      )}

      {copyingAutomationId && (
        <CopyAutomationModal
          show={!!copyingAutomationId}
          handleClose={() => setCopyingAutomationId(null)}
          automationId={copyingAutomationId}
          onAutomationCreated={fetchAutomations}
        />
      )}
    </DataTableContainer>
  )
}
